import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { NavigationIds } from "components/v2/shared-navigation/SharedNavigation";
import {
  getActiveTab,
  getCurrentUserId,
  getIsInitialUqualReferral,
  getNhDealApplicationId,
  getProspectId,
  getSelfReferralInSession,
  getUserRole,
} from "features/authSlice";
import { RegistrationRoleType } from "pages/registration/registrationService";
import { selectIsUqualAdBannerDismissed, setUqualAdBannerDismissed } from "features/uqualAdBannerSlice";
import { useGetDashboardItemsQuery, useCheckCreditScoreQuery } from "pages/dashboard/home/dashboardService";
import { useGetFeatureFlagsQuery } from "services/common/featureFlagService";
import { UQUAL_AD_BANNER_DISMISS_KEY } from "utilities/Constant";

export const useUqualAdBannerVisibility = () => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const userId = useSelector(getCurrentUserId);
  const activeTab = useSelector(getActiveTab);
  const nhDealApplicationId = useSelector(getNhDealApplicationId);
  const prospectId = useSelector(getProspectId);
  const selfReferralInSession = useSelector(getSelfReferralInSession);
  const isInitialUqualReferral = useSelector(getIsInitialUqualReferral);
  const bannerDismissed = useSelector(selectIsUqualAdBannerDismissed);
  const userRole = useSelector(getUserRole);
  const userRoleNormalized = (userRole || "").toLowerCase();

  const isOnDashboardHome = useMemo(() => {
    const onDashboardRoot = pathname === "/dashboard" || pathname === "/dashboard/";
    const isHomeTab = activeTab === "" || activeTab === NavigationIds.Home;
    return onDashboardRoot && isHomeTab;
  }, [pathname, activeTab]);

  const { data: featureFlag } = useGetFeatureFlagsQuery();

  const { data: dashboardData } = useGetDashboardItemsQuery(
    { dealApplicationId: nhDealApplicationId, userId: userId ?? "" },
    { skip: !isOnDashboardHome || !nhDealApplicationId || !userId },
  );

  const { data: isCreditScoreLow } = useCheckCreditScoreQuery(
    { nhDealApplicationId, prospectId },
    { skip: !isOnDashboardHome || !nhDealApplicationId || !prospectId },
  );

  const isUqualSelfReferralDone = dashboardData?.isUqualSelfReferralDone;
  const isFlagOn = Boolean(featureFlag?.uQualSelfReferralAdLogicEnabled);

  // Capture isNew once when the user first arrives on the dashboard and data is available.
  // Use isNew for borrowers and isCoborrowerNew for coborrowers.
  const wasNewOnLoadRef = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    if (wasNewOnLoadRef.current === undefined && isOnDashboardHome && dashboardData != null && dashboardData != undefined ) {
      const isSpousalCoBorrower = userRoleNormalized === RegistrationRoleType.SpousalCoBorrower.toLowerCase();
      wasNewOnLoadRef.current = isSpousalCoBorrower ? Boolean(dashboardData.isCoborrowerNew) : Boolean(dashboardData.isNew);
    }
  }, [isOnDashboardHome, dashboardData, userRoleNormalized]);
  const isNew = wasNewOnLoadRef.current;

  const isEligible = useMemo((): boolean => {
    if (!isCreditScoreLow || !isFlagOn || !isNew) return false;
    if (selfReferralInSession) return isInitialUqualReferral;
    return !isUqualSelfReferralDone;
  }, [isCreditScoreLow, isFlagOn, isNew, selfReferralInSession, isInitialUqualReferral, isUqualSelfReferralDone]);

  const isBannerVisible = useMemo(() => isOnDashboardHome && isEligible && !bannerDismissed, [isOnDashboardHome, isEligible, bannerDismissed]);

  const dismissBanner = useCallback(() => {
    sessionStorage.setItem(UQUAL_AD_BANNER_DISMISS_KEY, "true");
    dispatch(setUqualAdBannerDismissed(true));
  }, [dispatch]);

  return { isBannerVisible, dismissBanner };
};
